import { useState, useEffect, useRef, useCallback } from 'react';

import { BsQuestionCircle } from 'react-icons/bs';

import ProtocolModal from '#agentFeature/components/ProtocolModal';
import { isWorking } from '#agentFeature/constants/conversation';
import { useAssistantJobs } from '#agentFeature/hooks/useAssistantJobs';
import { useSetPopupOpen } from '#agentFeature/models/mutations';
import { profileToFilters } from '#agentFeature/utils/profileToFilters';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useErrorStore } from '#lib/stores/errorStore';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

import { fetchProspectQuery } from '../../api/prospectQueriesApi';
import AgenticSearchChat from '../../components/AgenticSearchChat/AgenticSearchChat';
import { LeadCriteriaModal } from '../../components/LeadOverviewPage/LeadCriteriaModal/LeadCriteriaModal';
import LeadOverviewContainer from '../../components/LeadOverviewPage/LeadOverviewContainer/LeadOverviewContainer';
import LeadSearchOverlay from '../../components/LeadSearchOverlay/LeadSearchOverlay';
import { useLeadData } from '../../contexts/LeadDataContext';
import { LeadOverviewProvider } from '../../contexts/LeadOverviewContext';
import { useLeadOverviewTour } from '../../hooks/useLeadOverviewTour';
import { useSearchFilters } from '../../hooks/useSearchFilters';
import { useSearchPreview } from '../../hooks/useSearchPreview';

/** The list item may omit `agentRunId`; `GET /leads/queries/:id` always carries it. */
async function resolveAgentRunId(query) {
	const known = query?.agentRunId ?? query?.input?.agentRunId;
	if (known) return known;
	const { data } = await fetchProspectQuery(query.id);
	return data?.agentRunId ?? null;
}

const LeadOverviewPageInner = () => {
	const { handleSearchQuerySelect, searchQueries, fetchingQueries, queriesFetched } = useLeadData();
	const showInfo = useErrorStore((s) => s.showInfo);
	const mode = usePreferencesStore((s) => s.leadSearchMode);
	const setMode = usePreferencesStore((s) => s.setLeadSearchMode);

	const [isCriteriaOpen, setIsCriteriaOpen] = useState(false);
	const [initialQueriesSettled, setInitialQueriesSettled] = useState(false);
	// A saved conversation reopened with "Mit Assistent fortsetzen"; otherwise the chat shows the draft.
	const [resumedRunId, setResumedRunId] = useState(null);
	// Every saved conversation continued in this session stays watched after the popup closed,
	// so work it still does shows in the job strip.
	const [watchedRunIds, setWatchedRunIds] = useState([]);
	const [protocolRunId, setProtocolRunId] = useState(null);

	const popupShowsChat = isCriteriaOpen && mode === 'agent';
	const { draft, extraJobs } = useAssistantJobs({
		watchedRunIds,
		visibleRunId: popupShowsChat ? (resumedRunId ?? 'draft') : null,
	});
	const draftName = draft ? draft.defaultName || 'Entwurf' : null;
	const chatRunId = resumedRunId ?? draft?.id ?? null;
	const setPopupOpen = useSetPopupOpen();

	// Paused until the first settled query result so the auto-started tour cannot
	// race the auto-opened criteria modal (on slow connections the queries fetch
	// outlasts the tour's start delay), and while the modal is open so the tour
	// never highlights elements underneath its overlay.
	const { startTour } = useLeadOverviewTour({ paused: !initialQueriesSettled || isCriteriaOpen });

	const {
		filters,
		searchType,
		isGermany,
		hasActiveHRFilters,
		hasActiveMapsFilters,
		handleFilterChange,
		handleStartSearch,
		handleSearchTypeToggle,
	} = useSearchFilters({ handleSearchQuerySelect });

	// Live, free Handelsregister match-count preview ("Treffer-Trichter").
	const {
		data: previewData,
		isLoading: previewIsLoading,
		isResolving: previewIsResolving,
	} = useSearchPreview({
		filters,
		searchType,
		isGermany,
		enabled: isCriteriaOpen && mode === 'form',
	});

	// Auto-open the popup once when the user genuinely has no prospect queries, so
	// the empty state guides them into their first search.
	//
	// The one-shot fires only on a *settled* result — the query has been fetched
	// and nothing is currently in flight. That's what makes an empty list
	// trustworthy: a transient empty state (initial load, or the refetch triggered
	// when arriving from onboarding where a search already exists) still has
	// isFetching=true and is skipped until the real result lands.
	useEffect(() => {
		if (initialQueriesSettled) return;
		if (!queriesFetched || fetchingQueries) return;

		setInitialQueriesSettled(true);
		if (searchQueries.length === 0) {
			setIsCriteriaOpen(true);
		}
	}, [initialQueriesSettled, queriesFetched, fetchingQueries, searchQueries.length]);

	// A reload restores the chat the user had open.
	const restoredRef = useRef(false);
	useEffect(() => {
		if (restoredRef.current || !draft?.popupOpen) return;
		restoredRef.current = true;
		setMode('agent');
		setIsCriteriaOpen(true);
	}, [draft?.popupOpen, setMode]);

	const openPopup = useCallback(() => {
		setIsCriteriaOpen(true);
		if (chatRunId && mode === 'agent') setPopupOpen.mutate({ runId: chatRunId, open: true });
	}, [chatRunId, mode, setPopupOpen]);

	const closePopup = useCallback(() => {
		setIsCriteriaOpen(false);
		if (chatRunId && mode === 'agent') setPopupOpen.mutate({ runId: chatRunId, open: false });
		setResumedRunId(null);
	}, [chatRunId, mode, setPopupOpen]);

	const handleStartSearchFromModal = async () => {
		await handleStartSearch();
		setIsCriteriaOpen(false);
	};

	const chat = {
		runId: chatRunId,
		onOpenForm: (profile, mandate) => {
			const { patch, dropped } = profileToFilters(profile, mandate);
			for (const [key, value] of Object.entries(patch)) handleFilterChange(key, value);
			if (dropped.length > 0) {
				showInfo({
					title: 'Kriterien nicht übernommen',
					message: `Die Suchmaske kennt keine freien Kriterien: ${dropped.join(', ')}. Sie bleiben im Assistenten erhalten und werden dort je Firma geprüft.`,
					autoClose: true,
					autoCloseDuration: 8000,
				});
			}
		},
		onSaved: ({ prospectQueryId }) => {
			handleSearchQuerySelect(prospectQueryId);
			setIsCriteriaOpen(false);
			setResumedRunId(null);
		},
		onDraftCreated: (run) => setPopupOpen.mutate({ runId: run.id, open: true }),
		onDiscarded: () => {
			setIsCriteriaOpen(false);
			setResumedRunId(null);
		},
	};

	return (
		<>
			{/* content mode: the lead table shrinks to the remaining space and scrolls
			    internally, keeping its horizontal scrollbar always visible. */}
			<Page
				width="full"
				scrollMode="content"
				className="bg-gray-50"
				header={
					<PageHeader breadcrumbs={['Lead-Übersicht']}>
						<LeadSearchOverlay />
						<button
							onClick={startTour}
							className="p-2 text-bgBase-800 hover:text-primary-600 hover:bg-purple-50 rounded-lg transition-colors"
							title="Tour starten"
						>
							<BsQuestionCircle className="w-5 h-5" />
						</button>
					</PageHeader>
				}
			>
				<LeadOverviewContainer
					onNewSearch={openPopup}
					draftBadge={draft ? { name: draftName, working: isWorking(draft.status) } : null}
					extraJobs={extraJobs}
					onResumeAssistant={async (query) => {
						const agentRunId = await resolveAgentRunId(query);
						if (!agentRunId) return;
						setResumedRunId(agentRunId);
						setWatchedRunIds((ids) => (ids.includes(agentRunId) ? ids : [...ids, agentRunId]));
						setMode('agent');
						setIsCriteriaOpen(true);
					}}
					onShowProtocol={async (query) => setProtocolRunId(await resolveAgentRunId(query))}
				/>
			</Page>

			<LeadCriteriaModal
				open={isCriteriaOpen}
				onClose={closePopup}
				filters={filters}
				searchType={searchType}
				isGermany={isGermany}
				hasActiveHRFilters={hasActiveHRFilters}
				hasActiveMapsFilters={hasActiveMapsFilters}
				onFilterChange={handleFilterChange}
				onStartSearch={handleStartSearchFromModal}
				onSearchTypeToggle={handleSearchTypeToggle}
				previewIsLoading={previewIsLoading}
				previewIsResolving={previewIsResolving}
				previewData={previewData}
				chat={chat}
			/>

			<ProtocolModal
				runId={protocolRunId}
				isOpen={Boolean(protocolRunId)}
				onClose={() => setProtocolRunId(null)}
			/>

			{mode === 'form' && <AgenticSearchChat />}
		</>
	);
};

const LeadOverviewPage = () => {
	return (
		<LeadOverviewProvider>
			<LeadOverviewPageInner />
		</LeadOverviewProvider>
	);
};

export default LeadOverviewPage;
